import { PublishingPlatform, PublishingStatus, PublishingDateRangeKey } from '../../domain/publishing-status';

/** Globe/internet icon used for France.tv (website) destination. */
export const GLOBE_ICON_PATH = '/assets/publishing-status/globe.svg';

/** Copy publication link (same glyph as evrideo-copy-button). */
export const COPY_LINK_ICON_PATH = '/assets/icons/copy-link.svg';

/** MAM media glyph for "Open in MAM" row action. */
export const MEDIA_ICON_PATH = '/assets/icons/media-icon.svg';

/**
 * Platform visual metadata — icon and brand identity.
 * Brand colors are set in SCSS, not hardcoded in TS.
 */
export interface PlatformMeta {
  label: string;
  icon: string;
  cssClass: string;
}

export const PLATFORM_META: Record<PublishingPlatform, PlatformMeta> = {
  [PublishingPlatform.YouTube]:   { label: 'YouTube',    icon: 'youtube.svg',    cssClass: 'platform--youtube' },
  [PublishingPlatform.Facebook]:  { label: 'Facebook',   icon: 'facebook.svg',   cssClass: 'platform--facebook' },
  [PublishingPlatform.Instagram]: { label: 'Instagram',  icon: 'instagram.svg',  cssClass: 'platform--instagram' },
  [PublishingPlatform.Twitter]:   { label: 'Twitter',    icon: 'twitter.svg',    cssClass: 'platform--twitter' },
  [PublishingPlatform.TikTok]:    { label: 'TikTok',     icon: 'tiktok.svg',     cssClass: 'platform--tiktok' },
  [PublishingPlatform.FranceTv]:  { label: 'France.tv',  icon: 'globe.svg',      cssClass: 'platform--website' }, // website: globe icon, same color as account
};

export const DEFAULT_PLATFORM_META: PlatformMeta = {
  label: 'Unknown',
  icon: 'share.svg',
  cssClass: 'platform--default',
};

/**
 * Status badge metadata — label and CSS class for semantic colors.
 * Colors are set in SCSS from _theme.scss ($success-main, $error-main, etc.).
 */
export interface StatusMeta {
  label: string;
  cssClass: string;
  /** Icon path for upload-status style (icon + label), from app assets/icons. */
  icon: string;
}

export const STATUS_META: Record<PublishingStatus, StatusMeta> = {
  [PublishingStatus.Success]: {
    label: 'Success',
    cssClass: 'status--success',
    icon: '/assets/icons/success.svg',
  },
  [PublishingStatus.SuccessWithWarnings]: {
    label: 'Success with warnings',
    cssClass: 'status--success-with-warnings',
    icon: '/assets/icons/success.svg',
  },
  [PublishingStatus.Failed]: {
    label: 'Failed',
    cssClass: 'status--failed',
    icon: '/assets/icons/failed.svg',
  },
  [PublishingStatus.Cancelled]: {
    label: 'Cancelled',
    cssClass: 'status--failed',
    icon: '/assets/icons/failed.svg',
  },
  [PublishingStatus.Scheduled]: {
    label: 'Scheduled',
    cssClass: 'status--scheduled',
    icon: '/assets/icons/schedule.svg',
  },
  [PublishingStatus.Rendering]: {
    label: 'Rendering',
    cssClass: 'status--processing',
    icon: '/assets/icons/pending.svg',
  },
  [PublishingStatus.Processing]: {
    label: 'Processing',
    cssClass: 'status--processing',
    icon: '/assets/icons/pending.svg',
  },
  [PublishingStatus.PostUploadProcessing]: {
    label: 'Post-upload processing',
    cssClass: 'status--processing',
    icon: '/assets/icons/pending.svg',
  },
  [PublishingStatus.Draft]: {
    label: 'Draft',
    cssClass: 'status--draft',
    icon: '/assets/icons/pending.svg',
  },
};

/** Table placeholders for incomplete Draft rows. */
export const DRAFT_DISPLAY_DEFAULTS = {
  noTitle: 'No title',
  missingField: '—',
} as const;

/** Background refresh interval for the publications list (ms). */
export const PUBLISHING_LIST_POLL_INTERVAL_MS = 15_000;

/** Date range options for the toolbar dropdown. */
export const PUBLISHING_DATE_RANGE_OPTIONS: { value: PublishingDateRangeKey; label: string }[] = [
  { value: 'all', label: 'All time' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'today', label: 'Today' },
  { value: 'last7', label: 'Last 7 days' },
  { value: 'last30', label: 'Last 30 days' },
];
